import type { DriveItem } from '@microsoft/microsoft-graph-types';
import { ConfidentialityLevel, DocumentType, type SharePointExternalRef } from '@repancy/core';

/**
 * Inputs to upsertFromExternal carved out of a Graph driveItem.
 * Repancy's documentType / confidentialityLevel are inferred heuristically
 * from filename + sensitivity labels; users can correct them after import.
 */
export interface MappedDocumentInput {
  title: string;
  documentType: DocumentType;
  confidentialityLevel: ConfidentialityLevel;
  externalRef: SharePointExternalRef;
}

const EXT_TO_TYPE: Record<string, DocumentType> = {
  '.docx': DocumentType.Brief,
  '.doc': DocumentType.Brief,
  '.pdf': DocumentType.Brief,
  '.txt': DocumentType.Memo,
  '.md': DocumentType.Memo,
};

const FILENAME_HINTS: Array<[RegExp, DocumentType]> = [
  [/motion/i, DocumentType.Motion],
  [/brief/i, DocumentType.Brief],
  [/contract|agreement|nda/i, DocumentType.Contract],
  [/memo/i, DocumentType.Memo],
  [/discovery|interrog/i, DocumentType.Discovery],
  [/letter|correspond/i, DocumentType.Correspondence],
  [/expert.*report/i, DocumentType.ExpertReport],
  [/due.?diligence/i, DocumentType.DueDiligenceChecklist],
  [/board.*resolution/i, DocumentType.BoardResolution],
  [/term.*sheet/i, DocumentType.TermSheet],
];

function inferDocumentType(name: string): DocumentType {
  for (const [pattern, type] of FILENAME_HINTS) {
    if (pattern.test(name)) return type;
  }
  const lower = name.toLowerCase();
  const dot = lower.lastIndexOf('.');
  if (dot >= 0) {
    const ext = lower.slice(dot);
    if (EXT_TO_TYPE[ext]) return EXT_TO_TYPE[ext];
  }
  return DocumentType.Memo;
}

/**
 * Convert SharePoint sensitivity-label / privileged folder hints into a
 * Repancy confidentiality level. Conservative default: Confidential.
 */
function inferConfidentiality(item: DriveItem): ConfidentialityLevel {
  const name = (item.name ?? '').toLowerCase();
  const path = (item.parentReference?.path ?? '').toLowerCase();
  if (/privileged|attorney.?client/.test(name) || /privileged/.test(path)) {
    return ConfidentialityLevel.AttorneyClientPrivileged;
  }
  if (/work.?product/.test(name) || /work.?product/.test(path)) {
    return ConfidentialityLevel.WorkProduct;
  }
  if (/public|filed/.test(name) || /public/.test(path)) {
    return ConfidentialityLevel.Public;
  }
  return ConfidentialityLevel.Confidential;
}

/**
 * Map a Graph driveItem to the fields upsertFromExternal expects.
 * Throws if the item lacks the IDs needed to round-trip.
 */
export function driveItemToInput(item: DriveItem, siteId: string): MappedDocumentInput {
  if (!item.id || !item.parentReference?.driveId || !item.eTag || !item.webUrl) {
    throw new Error(`driveItem ${item.id ?? '<no id>'} missing fields needed for upsertFromExternal`);
  }
  const title = item.name ?? 'Untitled';
  return {
    title,
    documentType: inferDocumentType(title),
    confidentialityLevel: inferConfidentiality(item),
    externalRef: {
      siteId,
      driveId: item.parentReference.driveId,
      itemId: item.id,
      etag: item.eTag,
      webUrl: item.webUrl,
    },
  };
}

/** Filter helper: a delta page mixes folders and files; we only import files. */
export function isFile(item: DriveItem): boolean {
  return Boolean(item.file) && !item.folder;
}

/** Filter helper: skip items the user (or SP) has tombstoned. */
export function isDeleted(item: DriveItem): boolean {
  // Graph marks deletions with a `deleted` facet on the item.
  // microsoft-graph-types calls it `deleted` (with state). Truthy means deleted.
  return Boolean((item as DriveItem & { deleted?: unknown }).deleted);
}
